import { Input, type InputVariantProps, inputVariants } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import * as SelectPrimitive from '@radix-ui/react-select'
import { TranslationKey, useTranslate } from '@tolgee/react'
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react'
import React, { createContext, useCallback, useContext, useState } from 'react'

const Select = SelectPrimitive.Root

const SelectGroup = SelectPrimitive.Group

const SelectValue = SelectPrimitive.Value

export const radioLikeCheckboxClass =
  'h-3 w-3 cursor-pointer appearance-none rounded-full border-2 border-mango-300 px-0 py-0 transition-colors checked:border-mango-300 checked:bg-black'

// context to store value-to-label mappings
const MultiSelectContext = createContext<{
  valueToLabelMap: Map<string, string>
}>({
  valueToLabelMap: new Map(),
})

type SelectTriggerProps = React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> & {
  isDirty?: boolean
  compact?: boolean
  multiselect?: boolean
  selectedValues?: string[]
  placeholder?: string
} & Omit<InputVariantProps, 'height'>

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  SelectTriggerProps
>(
  (
    {
      className,
      children,
      isDirty,
      error,
      text,
      variant,
      disabled,
      compact,
      multiselect,
      selectedValues,
      placeholder,
      ...props
    },
    ref,
  ) => {
    const { valueToLabelMap } = useContext(MultiSelectContext)

    const displayText = React.useMemo(() => {
      if (multiselect && selectedValues && selectedValues.length > 0) {
        // Map selected values to their display labels
        const labels = selectedValues.map((value) => valueToLabelMap.get(value) || value).join(', ')
        return labels
      }
      return null
    }, [multiselect, selectedValues, valueToLabelMap])

    return (
      <SelectPrimitive.Trigger
        ref={ref}
        tabIndex={0}
        className={cn(
          '[&>span]:line-clamp-1',
          compact
            ? 'relative flex h-8 w-[164px] select-none items-center justify-between whitespace-nowrap rounded border border-neutral-75 bg-neutral-white px-2 text-sm font-light text-neutral-500 shadow-none hover:bg-neutral-50 focus:outline-0 active:bg-neutral-50 disabled:bg-neutral-50'
            : variant === 'black'
              ? 'relative flex h-10 w-full select-none items-center justify-between whitespace-nowrap rounded border-none bg-neutral-600 px-3 text-sm font-medium text-white placeholder:text-white'
              : inputVariants({ variant, text, error }),
          !compact &&
            (variant === 'black'
              ? '[&>span]:text-white'
              : isDirty || (multiselect && selectedValues && selectedValues.length > 0)
                ? '[&>span]:text-neutral-500'
                : '[&>span]:italic [&>span]:text-neutral-150'),

          className,
        )}
        {...props}
      >
        {multiselect ? (
          <span className='overflow-hidden truncate text-ellipsis whitespace-nowrap'>
            {displayText || placeholder || 'Select options...'}
          </span>
        ) : (
          children
        )}
        <SelectPrimitive.Icon asChild>
          {!disabled && (
            <ChevronDownIcon
              className={cn(
                'size-4 shrink-0',
                variant === 'black' ? 'text-mango-400' : compact && 'text-neutral-150',
              )}
            />
          )}
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
    )
  },
)

SelectTrigger.displayName = SelectPrimitive.Trigger.displayName

const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn('flex cursor-default items-center justify-center py-1', className)}
    {...props}
  >
    <ChevronUpIcon className='shrink-0' />
  </SelectPrimitive.ScrollUpButton>
))
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn('flex cursor-default items-center justify-center py-1', className)}
    {...props}
  >
    <ChevronDownIcon />
  </SelectPrimitive.ScrollDownButton>
))
SelectScrollDownButton.displayName = SelectPrimitive.ScrollDownButton.displayName

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = 'popper', ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      className={cn(
        'relative z-50 max-h-96 overflow-hidden rounded-md border bg-neutral-white shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
        position === 'popper' &&
          'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
        className,
      )}
      position={position}
      {...props}
    >
      <SelectScrollUpButton />
      <SelectPrimitive.Viewport
        className={cn(
          'p-1',
          position === 'popper' &&
            'h-[var(--radix-select-trigger-height)] w-[var(--radix-select-trigger-width)]',
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
      <SelectScrollDownButton />
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
))
SelectContent.displayName = SelectPrimitive.Content.displayName

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn('px-2 py-1.5 text-sm font-semibold', className)}
    {...props}
  />
))
SelectLabel.displayName = SelectPrimitive.Label.displayName

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      'relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-2 pr-8 text-sm text-neutral-500 outline-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 hover:cursor-pointer focus:bg-neutral-50',
      className,
    )}
    {...props}
  >
    <span className='absolute right-2 flex h-3.5 w-3.5 items-center justify-center'>
      <SelectPrimitive.ItemIndicator>
        <CheckIcon className='size-4 text-mango-300' />
      </SelectPrimitive.ItemIndicator>
    </span>
    <SelectPrimitive.ItemText className='whitespace-normal break-words pr-2'>
      {children}
    </SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
))
SelectItem.displayName = SelectPrimitive.Item.displayName

/** SelectItem from translation key */
const SelectItemFromKey = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  Omit<React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>, 'value' | 'children'> & {
    translationKey: TranslationKey
  }
>(({ translationKey, ...props }, ref) => {
  const { t } = useTranslate()
  const value = t(translationKey)
  return (
    <SelectItem ref={ref} value={value} {...props}>
      {value}
    </SelectItem>
  )
})
SelectItemFromKey.displayName = 'SelectItemFromKey'

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn('-mx-1 my-1 h-px bg-neutral-150', className)}
    {...props}
  />
))
SelectSeparator.displayName = SelectPrimitive.Separator.displayName

// MultiSelect Components
interface MultiSelectProps {
  children: React.ReactNode
  selectedValues: string[]
  onSelectionChange: (values: string[]) => void
  placeholder?: string
  className?: string
  triggerProps?: Omit<SelectTriggerProps, 'multiselect' | 'selectedValues' | 'placeholder'>
}

const MultiSelect: React.FC<MultiSelectProps> = ({
  children,
  selectedValues,
  onSelectionChange,
  placeholder,
  className,
  triggerProps,
}) => {
  const [open, setOpen] = useState(false)

  // Build the value-to-label mapping from children
  const valueToLabelMap = React.useMemo(() => {
    const map = new Map<string, string>()

    React.Children.forEach(children, (child) => {
      if (React.isValidElement(child) && child.type === MultiSelectItem) {
        const value = child.props.value
        const label =
          typeof child.props.children === 'string'
            ? child.props.children
            : React.Children.toArray(child.props.children).join('')
        map.set(value, label)
      }
    })

    return map
  }, [children])

  return (
    <MultiSelectContext.Provider value={{ valueToLabelMap }}>
      <Select open={open} onOpenChange={setOpen}>
        <SelectTrigger
          {...triggerProps}
          multiselect
          selectedValues={selectedValues}
          placeholder={placeholder}
          className={className}
        />
        <SelectContent>
          <div onClick={(e) => e.stopPropagation()}>
            {React.Children.map(children, (child) => {
              if (React.isValidElement(child) && child.type === MultiSelectItem) {
                return React.cloneElement(child, {
                  selectedValues,
                  onSelectionChange,
                } as any)
              }
              return child
            })}
          </div>
        </SelectContent>
      </Select>
    </MultiSelectContext.Provider>
  )
}

interface MultiSelectItemProps {
  value: string
  children: React.ReactNode
  selectedValues?: string[]
  onSelectionChange?: (values: string[]) => void
  className?: string
  disabled?: boolean
}

const MultiSelectItem: React.FC<MultiSelectItemProps> = ({
  value,
  children,
  selectedValues = [],
  onSelectionChange,
  className,
  disabled = false,
}) => {
  const isSelected = selectedValues.includes(value)

  const handleToggle = useCallback(() => {
    if (!onSelectionChange || disabled) return

    const newSelection = isSelected
      ? selectedValues.filter((v) => v !== value)
      : [...selectedValues, value]

    onSelectionChange(newSelection)
  }, [isSelected, selectedValues, value, onSelectionChange, disabled])

  return (
    <div
      className={cn(
        'relative flex w-full select-none items-start rounded-sm py-1.5 pl-2 pr-8 text-sm outline-none',
        disabled ? 'text-neutral-300' : 'cursor-pointer text-neutral-500 hover:bg-neutral-50',
        className,
      )}
      onClick={handleToggle}
    >
      <Input
        type='checkbox'
        checked={isSelected}
        onChange={() => {}}
        disabled={disabled}
        className={cn(radioLikeCheckboxClass, 'mr-2 mt-0.5 flex-shrink-0')}
      />
      <span className='flex-1 whitespace-normal break-words pr-2'>{children}</span>
    </div>
  )
}

export {
  MultiSelect,
  MultiSelectItem,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectItemFromKey,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
  type SelectTriggerProps,
}
